import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Search, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useInfluencerByHandle } from "@/hooks/useInfluencerByHandle";
import InfluencerDetail from "@/components/InfluencerDetail";
import { Progress } from "@/components/ui/progress";
import PostCheckoutLoginModal from "@/components/PostCheckoutLoginModal";

import { useAuth } from "@/contexts/AuthContext";

function useOgMeta(handle: string | undefined, displayName: string | undefined) {
  useEffect(() => {
    if (!handle || !displayName) return;
    const cleanHandle = handle.replace(/^@/, "");
    const ogImageUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/og-image?handle=${cleanHandle}`;
    const profileUrl = `https://finfluenceraudit.pages.dev/profile/${cleanHandle}`;

    document.title = `${displayName} — TrustFinTwit Audit`;

    const setMeta = (property: string, content: string, attr = "property") => {
      let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, property);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta("og:title", `${displayName} — TrustFinTwit Audit`);
    setMeta("og:image", ogImageUrl);
    setMeta("og:url", profileUrl);
    setMeta("og:description", `Check ${displayName}'s financial call track record on TrustFinTwit.`);
    setMeta("twitter:title", `${displayName} — TrustFinTwit Audit`, "name");
    setMeta("twitter:image", ogImageUrl, "name");
    setMeta("twitter:description", `Check ${displayName}'s financial call track record on TrustFinTwit.`, "name");

    return () => {
      document.title = "TrustFinTwit - Finfluencer Audit";
    };
  }, [handle, displayName]);
}

const ProfilePage = () => {
  const { handle } = useParams<{ handle: string }>();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [verifiedXUserId, setVerifiedXUserId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [showPostCheckoutLogin, setShowPostCheckoutLogin] = useState(false);
  const [initPhase, setInitPhase] = useState<"fetching" | "classifying" | null>(null);
  const initStartedRef = useRef(false);

  const analyzing = searchParams.get("analyzing") === "1";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [handle]);

  // Handle verified X user ID from OAuth callback redirect
  useEffect(() => {
    const verifiedId = searchParams.get("verified_x_user_id");
    if (verifiedId) {
      setVerifiedXUserId(verifiedId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle claimed=true redirect from Stripe
  useEffect(() => {
    if (searchParams.get("claimed") === "true") {
      toast.success("Profile claimed successfully! 🎉");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle subscribed=true redirect from Stripe checkout
  useEffect(() => {
    if (searchParams.get("subscribed") === "true") {
      setSearchParams({}, { replace: true });
      if (!user) {
        setShowPostCheckoutLogin(true);
      } else {
        toast.success("Subscription activated! You can now follow influencers. 🎉");
      }
    }
  }, [searchParams, setSearchParams, user]);

  const navigate = useNavigate();
  const cleanHandle = handle?.replace(/^@/, "") || "";
  const { data: influencer, isLoading, refetch } = useInfluencerByHandle(handle);
  useOgMeta(handle, influencer?.displayName);

  // When ?analyzing=1, kick off fetch-tweets + classify-tweets in background
  useEffect(() => {
    if (!analyzing || !cleanHandle || initStartedRef.current) return;
    initStartedRef.current = true;
    setIsInitializing(true);
    setInitPhase("fetching");

    // Remove analyzing param from URL
    setSearchParams({}, { replace: true });

    const callFunction = async (name: string, body: Record<string, unknown>) => {
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || `${name} failed`);
      }
      return resp.json();
    };

    (async () => {
      try {
        const fetchResult = await callFunction("fetch-tweets", { handle: cleanHandle });
        toast.success(
          `Fetched ${fetchResult.tweets_fetched} tweets for @${fetchResult.user?.handle?.replace("@", "") || cleanHandle}`
        );
        // Refetch the influencer data now that user + tweets exist
        refetch();

        setInitPhase("classifying");
        // Fire classification in background
        callFunction("classify-tweets", {}).then(() => {
          refetch();
        }).catch(() => {});

        setIsInitializing(false);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Unknown error";
        toast.error(`Analysis failed: ${msg}`);
        setIsInitializing(false);
        setInitPhase(null);
      }
    })();
  }, [analyzing, cleanHandle]);

  // Show initializing state
  if (isInitializing || (analyzing && isLoading)) {
    return (
      <main className="min-h-screen bg-background bg-grid">
        <div className="max-w-4xl mx-auto px-4 pt-6 pb-20">
          <div className="sticky top-2 z-20 -mx-4 px-4 py-2">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm bg-card/80 backdrop-blur-sm border border-border rounded-full px-4 py-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
          <div className="bg-card border border-border rounded-xl p-8 space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-secondary animate-pulse" />
              <div>
                <h2 className="text-2xl font-bold text-foreground">@{cleanHandle}</h2>
                <p className="text-sm text-muted-foreground font-mono">Setting up profile...</p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Step 1: Fetching */}
              <div className="flex items-center gap-3">
                {initPhase === "fetching" ? (
                  <Loader2 className="w-5 h-5 text-primary animate-spin shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-profit/20 flex items-center justify-center shrink-0">
                    <div className="w-2 h-2 rounded-full bg-profit" />
                  </div>
                )}
                <div>
                  <p className={`text-sm font-medium ${initPhase === "fetching" ? "text-foreground" : "text-muted-foreground"}`}>
                    {initPhase === "fetching" ? "Scanning timeline for financial tweets..." : "Tweets fetched"}
                  </p>
                  {initPhase === "fetching" && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      This may take a moment for accounts with extensive history.
                    </p>
                  )}
                </div>
              </div>

              {/* Step 2: Classifying */}
              <div className="flex items-center gap-3">
                {initPhase === "classifying" ? (
                  <Loader2 className="w-5 h-5 text-primary animate-spin shrink-0" />
                ) : initPhase === "fetching" ? (
                  <div className="w-5 h-5 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <div className="w-2 h-2 rounded-full bg-muted-foreground" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-profit/20 flex items-center justify-center shrink-0">
                    <div className="w-2 h-2 rounded-full bg-profit" />
                  </div>
                )}
                <p className={`text-sm font-medium ${initPhase === "classifying" ? "text-foreground" : "text-muted-foreground"}`}>
                  {initPhase === "classifying" ? "Classifying tweets..." : "Classify & analyze calls"}
                </p>
              </div>
            </div>

            {initPhase === "fetching" && (
              <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                <div className="h-full bg-primary rounded-full animate-pulse w-1/3" />
              </div>
            )}
          </div>
        </div>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main className="min-h-screen bg-background bg-grid flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </main>
    );
  }

  if (!influencer) {
    return (
      <main className="min-h-screen bg-background bg-grid">
        <div className="max-w-4xl mx-auto px-4 pt-6 pb-20 text-center">
          <p className="text-muted-foreground font-mono">
            No data found for @{handle}. Go back and analyze this handle first.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm bg-card/80 border border-border rounded-full px-4 py-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background bg-grid">
      <div className="w-[95vw] sm:w-[90vw] lg:w-[70vw] mx-auto px-4 pt-6 pb-8">
        <InfluencerDetail
          influencer={influencer}
          onBack={() => navigate(-1)}
          verifiedXUserId={verifiedXUserId}
          initialSortKey={searchParams.get("sort") as any}
          initialSortDir={searchParams.get("dir") as any}
          expandAsset={searchParams.get("expandAsset") || searchParams.get("asset")}
          expandCallId={searchParams.get("expandCallId")}
        />
      </div>
      {/* Ad sidebar hidden for now */}
      <PostCheckoutLoginModal
        open={showPostCheckoutLogin}
        onOpenChange={setShowPostCheckoutLogin}
      />
    </main>
  );
};

export default ProfilePage;
